import React, { useContext, useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import BigModal from '../../../components/Modals/BigModal'

const NONE = ''

// Delete a base location after re-homing everything that points at it.
// The server lists every dependent record by category; here each record
// gets a destination — chosen per record (one tick per row), per category
// ("Move all Events to…"), or for everything at once. Delete only unlocks
// once every record has somewhere to go.
const DeleteBaseLocationDialog = ({ open, onClose, base, onDeleted }) => {
  const { setToast } = useContext(GlobalContext)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [data, setData] = useState(null)
  // { [categoryKey]: { default: destId|'', records: { [recordId]: destId } } }
  const [assignments, setAssignments] = useState({})
  const [everything, setEverything] = useState(NONE)
  const [confirmed, setConfirmed] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [serverError, setServerError] = useState('')

  useEffect(() => {
    if (!open || !base?._id) return
    setLoading(true)
    setFetchError(false)
    setData(null)
    setAssignments({})
    setEverything(NONE)
    setConfirmed(false)
    setServerError('')
    API.get(`/base-locations/${base._id}/dependents`)
      .then((res) => {
        if (res.status === 200) setData(res.data)
        else setFetchError(true)
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [open, base?._id])

  const destinations = data?.destinations || []
  const categories = data?.categories || []

  const effectiveDestination = (categoryKey, recordId) =>
    assignments[categoryKey]?.records?.[recordId] ||
    assignments[categoryKey]?.default ||
    NONE

  const setCategoryDefault = (categoryKey, dest) =>
    setAssignments((prev) => ({
      ...prev,
      // A category-wide choice resets any per-record overrides in it.
      [categoryKey]: { default: dest, records: {} },
    }))

  const setEverythingTo = (dest) => {
    setEverything(dest)
    setAssignments(
      Object.fromEntries(
        categories.map((category) => [
          category.key,
          { default: dest, records: {} },
        ]),
      ),
    )
  }

  const setRecordDestination = (categoryKey, recordId, dest) => {
    setEverything(NONE)
    setAssignments((prev) => ({
      ...prev,
      [categoryKey]: {
        default: prev[categoryKey]?.default || NONE,
        records: { ...(prev[categoryKey]?.records || {}), [recordId]: dest },
      },
    }))
  }

  const progress = useMemo(() => {
    let total = 0
    let assigned = 0
    let truncatedWithoutDefault = []
    categories.forEach((category) => {
      total += category.total
      const defaultDest = assignments[category.key]?.default
      category.records.forEach((record) => {
        if (effectiveDestination(category.key, record.id)) assigned += 1
      })
      const unlisted = category.total - category.records.length
      if (unlisted > 0) {
        if (defaultDest) assigned += unlisted
        else truncatedWithoutDefault.push(category.label)
      }
    })
    return { total, assigned, truncatedWithoutDefault }
  }, [categories, assignments])

  const allAssigned = progress.total === progress.assigned
  const canDelete = data?.canDelete && allAssigned && confirmed && !deleting

  const handleDelete = () => {
    setDeleting(true)
    setServerError('')
    const payload = Object.fromEntries(
      categories.map((category) => [
        category.key,
        {
          default: assignments[category.key]?.default || null,
          records: assignments[category.key]?.records || {},
        },
      ]),
    )
    API.post(`/base-locations/${base._id}/delete`, { assignments: payload })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: `${base.name} deleted` })
          onDeleted?.(res.data)
        } else {
          setServerError(
            res.data?.error ||
              'Unable to delete this base location. Please try again.',
          )
        }
      })
      .catch(() =>
        setServerError('Unable to delete this base location. Please try again.'),
      )
      .finally(() => setDeleting(false))
  }

  return (
    <BigModal open={open} onClose={deleting ? undefined : onClose}>
      <Stack spacing={2} sx={{ minWidth: { md: 720 } }}>
        <Box>
          <Typography variant="title2">Delete {base?.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Everything currently attached to this base must be moved to another
            base location first. Choose a destination for each record, or set
            one for a whole category or for everything at once.
          </Typography>
        </Box>

        {loading && <LinearProgress />}
        {fetchError && (
          <Alert severity="error" variant="filled">
            Unable to load the records attached to this base. Please try again.
          </Alert>
        )}

        {data && !data.canDelete && (
          <Alert severity="warning">
            This is your only base location, so it can't be deleted. Add another
            base first, then move this base's records across to it.
          </Alert>
        )}

        {data && data.canDelete && (
          <>
            {categories.length === 0 ? (
              <Alert severity="info">
                Nothing is attached to this base — it can be deleted straight
                away.
              </Alert>
            ) : (
              <>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Box flex={1}>
                      <Typography fontWeight={700}>Move everything to</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sets the destination for all {progress.total} record
                        {progress.total === 1 ? '' : 's'} below. You can still
                        change individual categories or records afterwards.
                      </Typography>
                    </Box>
                    <TextField
                      select
                      size="small"
                      value={everything}
                      onChange={(e) => setEverythingTo(e.target.value)}
                      sx={{ minWidth: 220 }}
                      label="Destination"
                    >
                      <MenuItem value={NONE}>
                        <em>Choose a location</em>
                      </MenuItem>
                      {destinations.map((dest) => (
                        <MenuItem key={dest._id} value={dest._id}>
                          {dest.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Paper>

                {categories.map((category) => (
                  <Stack key={category.key} spacing={1}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flex={1}
                      >
                        <Typography variant="h6" fontWeight={700}>
                          {category.label}
                        </Typography>
                        <Chip
                          size="small"
                          label={category.total}
                          sx={{ borderRadius: 1, fontWeight: 700 }}
                        />
                      </Stack>
                      <TextField
                        select
                        size="small"
                        value={assignments[category.key]?.default || NONE}
                        onChange={(e) => {
                          setEverything(NONE)
                          setCategoryDefault(category.key, e.target.value)
                        }}
                        sx={{ minWidth: 220 }}
                        label={`Move all ${category.label.toLowerCase()} to`}
                      >
                        <MenuItem value={NONE}>
                          <em>Choose per record</em>
                        </MenuItem>
                        {destinations.map((dest) => (
                          <MenuItem key={dest._id} value={dest._id}>
                            {dest.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ borderRadius: 2, maxHeight: 320 }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Record</TableCell>
                            {destinations.map((dest) => (
                              <TableCell
                                key={dest._id}
                                align="center"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {dest.name}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {category.records.map((record) => {
                            const current = effectiveDestination(
                              category.key,
                              record.id,
                            )
                            return (
                              <TableRow
                                key={record.id}
                                hover
                                sx={{
                                  bgcolor: current ? undefined : 'rgba(242,139,31,0.06)',
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>
                                    {record.label}
                                  </Typography>
                                  {record.sub && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {record.sub}
                                    </Typography>
                                  )}
                                </TableCell>
                                {destinations.map((dest) => (
                                  <TableCell
                                    key={dest._id}
                                    align="center"
                                    padding="checkbox"
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={current === dest._id}
                                      onChange={() =>
                                        setRecordDestination(
                                          category.key,
                                          record.id,
                                          dest._id,
                                        )
                                      }
                                      inputProps={{
                                        'aria-label': `Move ${record.label} to ${dest.name}`,
                                      }}
                                    />
                                  </TableCell>
                                ))}
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {category.truncated && (
                      <Typography variant="caption" color="text.secondary">
                        Showing the first {category.records.length} of{' '}
                        {category.total}. The remaining{' '}
                        {category.total - category.records.length} will follow
                        the "Move all {category.label.toLowerCase()} to" choice
                        above.
                      </Typography>
                    )}
                  </Stack>
                ))}
              </>
            )}

            {serverError && <Alert severity="error">{serverError}</Alert>}

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor: allAssigned ? 'success.main' : 'warning.main',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <Box flex={1}>
                  <Typography fontWeight={700}>
                    {progress.total === 0
                      ? 'No records to move'
                      : `${progress.assigned} of ${progress.total} record${
                          progress.total === 1 ? '' : 's'
                        } have a destination`}
                  </Typography>
                  {!allAssigned && (
                    <Typography variant="caption" color="text.secondary">
                      {progress.truncatedWithoutDefault.length
                        ? `Set a "Move all" choice for: ${progress.truncatedWithoutDefault.join(', ')}. `
                        : ''}
                      Highlighted rows still need a destination.
                    </Typography>
                  )}
                  <FormControlLabel
                    sx={{ display: 'flex', mt: 0.5 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I understand that deleting {base?.name} cannot be undone.
                      </Typography>
                    }
                  />
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={onClose} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteForeverIcon />}
                    disabled={!canDelete}
                    onClick={handleDelete}
                  >
                    {deleting ? 'Deleting...' : 'Delete base location'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </>
        )}
      </Stack>
    </BigModal>
  )
}

export default DeleteBaseLocationDialog
